import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import {
  buildEntityFormData,
  mapLocalizationsFromApi,
} from '../../shared/utils/buildEntityFormData.js';

const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'text', required: true },
  { name: 'description', label: 'Описание', type: 'textarea', rows: 4 },
  { name: 'eventOrganizer', label: 'Организатор', type: 'text' },
  {
    name: 'eventMembers',
    label: 'Участники',
    type: 'textarea',
    hint: 'Спикеры, гости, компании-участники',
  },
  { name: 'imgPath', label: 'Обложка', type: 'image', entityType: 'event' },
  { name: 'seoMetatags', label: 'SEO', type: 'seo' },
];

// У события часовой пояс — строка; на сайте показывается как есть
const TIMEZONES = [
  'UTC-08:00',
  'UTC-05:00',
  'UTC+00:00',
  'UTC+01:00',
  'UTC+02:00',
  'UTC+03:00',
  'UTC+04:00',
  'UTC+05:00',
  'UTC+05:30',
  'UTC+06:00',
  'UTC+07:00',
  'UTC+08:00',
  'UTC+09:00',
  'UTC+10:00',
];

const emptyForm = {
  title: '',
  description: '',
  eventOrganizer: '',
  eventMembers: '',
  eventPlace: '',
  startDate: '',
  endDate: '',
  timeZone: 'UTC+05:00',
  categoryId: 0,
  formatId: 0,
  imgPath: '',
  seoMetatags: { title: '', description: '', keywords: '' },
};

// "2025-05-01T10:00:00" → значение для <input type="datetime-local">
const toInputDate = (value) => (value ? String(value).slice(0, 16) : '');

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Создание и редактирование мероприятия: основные поля, обложка, SEO и локализации — одним запросом
 */
const EventFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [localizations, setLocalizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [categoriesRes, formatsRes, event] = await Promise.all([
          apiRequests.events.getCategories(200),
          apiRequests.events.getFormats(200),
          isEdit ? apiRequests.events.getById(id) : Promise.resolve(null),
        ]);
        const categoryItems = categoriesRes.items || [];
        const formatItems = formatsRes.items || [];
        setCategories(categoryItems);
        setFormats(formatItems);

        if (event) {
          setForm({
            id: event.id,
            title: event.title || '',
            description: event.description || '',
            eventOrganizer: event.eventOrganizer || '',
            eventMembers: event.eventMembers || '',
            eventPlace: event.eventPlace || '',
            startDate: toInputDate(event.startDate),
            endDate: toInputDate(event.endDate),
            timeZone: event.timeZone || '',
            categoryId: event.categoryId || event.category?.id || categoryItems[0]?.id || 0,
            formatId: event.formatId || event.format?.id || formatItems[0]?.id || 0,
            imgPath: event.imgPath || '',
            seoMetatags: event.seoMetatags || emptyForm.seoMetatags,
          });
          setLocalizations(mapLocalizationsFromApi(event.localizations, []));
        } else {
          setForm({
            ...emptyForm,
            categoryId: categoryItems[0]?.id || 0,
            formatId: formatItems[0]?.id || 0,
          });
        }
      } catch (error) {
        toast.error(getErrorMessage(error, 'Не удалось загрузить данные мероприятия'));
      }
    };
    load();
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.title.trim()) {
      toast.error('Введите название мероприятия');
      return;
    }
    if (!form.categoryId || !form.formatId) {
      toast.error('Выберите категорию и формат — без них мероприятие не сохранится');
      return;
    }
    if (!form.startDate) {
      toast.error('Укажите дату начала');
      return;
    }
    if (form.endDate && form.endDate < form.startDate) {
      toast.error('Дата окончания раньше даты начала');
      return;
    }
    if (!isEdit && !mainImage) {
      toast.error('Загрузите обложку мероприятия');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      // imgPath — только для превью: обложка уходит файлом mainImg
      const model = { ...form, endDate: form.endDate || form.startDate };
      delete model.imgPath;
      const body = buildEntityFormData({ model, mainImage, contentKeys: [], localizations });

      if (isEdit) {
        await apiRequests.events.update(body);
        toast.success('Мероприятие сохранено');
      } else {
        await apiRequests.events.create(body);
        toast.success('Мероприятие создано');
        setTimeout(() => navigate('/events'), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить мероприятие'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  const timezoneOptions =
    form.timeZone && !TIMEZONES.includes(form.timeZone) ? [form.timeZone, ...TIMEZONES] : TIMEZONES;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/events')}
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Создать мероприятие'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="Например, Alfateam Tech Day"
              />
            </FormField>

            <FormField label="Описание">
              <Form.Control
                as="textarea"
                rows={4}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </FormField>

            <Row>
              <Col md={6}>
                <FormField label="Категория" required>
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setField('categoryId', Number(e.target.value))}
                  >
                    {categories.length === 0 && (
                      <option value={0}>Сначала создайте категорию</option>
                    )}
                    {categories.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.title}
                      </option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Формат" required>
                  <Form.Select
                    value={form.formatId}
                    onChange={(e) => setField('formatId', Number(e.target.value))}
                  >
                    {formats.length === 0 && <option value={0}>Сначала создайте формат</option>}
                    {formats.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.title}
                      </option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormField label="Организатор">
                  <Form.Control
                    type="text"
                    value={form.eventOrganizer}
                    onChange={(e) => setField('eventOrganizer', e.target.value)}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Место проведения" hint="Адрес или «Онлайн»">
                  <Form.Control
                    type="text"
                    value={form.eventPlace}
                    onChange={(e) => setField('eventPlace', e.target.value)}
                  />
                </FormField>
              </Col>
            </Row>

            <FormField label="Участники" hint="Спикеры, гости, компании-участники">
              <Form.Control
                as="textarea"
                rows={2}
                value={form.eventMembers}
                onChange={(e) => setField('eventMembers', e.target.value)}
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Обложка" required={!isEdit}>
              <UploadPicture
                onSelect={setMainImage}
                initialValue={form.imgPath || undefined}
                entityType="event"
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Дата и время">
        <Row>
          <Col md={4}>
            <FormField label="Начало" required>
              <Form.Control
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <FormField label="Окончание" hint="Если пусто — совпадает с началом">
              <Form.Control
                type="datetime-local"
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => setField('endDate', e.target.value)}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <FormField label="Часовой пояс">
              <Form.Select
                value={form.timeZone}
                onChange={(e) => setField('timeZone', e.target.value)}
              >
                <option value="">Не указан</option>
                {timezoneOptions.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Form.Select>
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="SEO">
        <SEOMetatagsForm
          seoMetatags={form.seoMetatags}
          onChange={(seo) => setField('seoMetatags', seo)}
        />
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        source={form}
        aiContext="мероприятие"
      />
    </FormLayout>
  );
};

export default EventFormPage;
